import * as React from 'react';
import { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { useNotify, useRecordContext } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import QRCode from 'qrcode';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import * as Sentry from '@sentry/react';
import { partnerQrUrl, type Partner } from '../../services/marketing/marketingService.ts';

const escapeHtml = (s: string) =>
    s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

/**
 * Prints the QR code, the partner's name and the link, and nothing else: a
 * one-off page in a hidden frame, so the panel's sidebar and form never reach
 * the paper and no pop-up blocker gets in the way.
 */
const printCard = (qrDataUrl: string, name: string, link: string, scanText: string) => {
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    Object.assign(frame.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
    document.body.appendChild(frame);
    const doc = frame.contentDocument;
    const win = frame.contentWindow;
    if (!doc || !win) {
        frame.remove();
        return;
    }
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(name)}</title>
<style>
  @page { margin: 18mm; }
  body { font-family: Helvetica, Arial, sans-serif; text-align: center; color: #111; margin: 0; }
  h1 { font-size: 26pt; margin: 0 0 8mm; }
  img { width: 110mm; height: 110mm; image-rendering: pixelated; }
  p { font-size: 13pt; margin: 6mm 0 0; }
  .link { font-size: 15pt; font-weight: bold; word-break: break-all; }
</style></head><body>
  <h1>${escapeHtml(name)}</h1>
  <img src="${qrDataUrl}" alt="">
  <p>${escapeHtml(scanText)}</p>
  <p class="link">${escapeHtml(link)}</p>
</body></html>`);
    doc.close();
    const img = doc.querySelector('img');
    const go = () => {
        win.focus();
        win.print();
        // Long enough for the print dialog to have taken its copy.
        setTimeout(() => frame.remove(), 1000);
    };
    if (img && !img.complete) img.onload = go;
    else go();
};

/**
 * On a partner's page: the link to hand out, with a copy button, and the QR
 * code for the counter card, with a print button. The QR code carries the
 * partner code and utm fields so a scan is told apart from a typed link.
 */
const PartnerLinkPanel: React.FC = () => {
    const record = useRecordContext<Partner>();
    const notify = useNotify();
    const { t } = useTranslation();
    const [qr, setQr] = useState<string | null>(null);

    const code = record?.code ?? '';
    const link = record?.link || (code ? `https://thevello.com/join/${encodeURIComponent(code)}` : '');
    const qrUrl = code ? partnerQrUrl(code) : '';

    useEffect(() => {
        if (!qrUrl) return;
        let cancelled = false;
        QRCode.toDataURL(qrUrl, { width: 600, margin: 2, errorCorrectionLevel: 'M' })
            .then((url) => { if (!cancelled) setQr(url); })
            .catch((e) => Sentry.captureException(e, { tags: { source: 'partner-qr' } }));
        return () => { cancelled = true; };
    }, [qrUrl]);

    if (!record) return null;

    const copy = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            notify(t('marketing.partners.copied'), { type: 'success' });
        } catch {
            notify(t('marketing.partners.copyFailed'), { type: 'warning' });
        }
    };

    return (
        <Card variant="outlined" sx={{ m: 2 }}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
                    <Typography variant="h6">{t('marketing.partners.linkTitle')}</Typography>
                    <Button
                        component={Link}
                        to={`/marketing/partner-statement?partnerId=${record.id}`}
                        startIcon={<ReceiptLongIcon />}
                    >
                        {t('marketing.partners.openStatement')}
                    </Button>
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'flex-start' }}>
                    <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                                size="small"
                                fullWidth
                                label={t('marketing.partners.link')}
                                value={link}
                                InputProps={{ readOnly: true }}
                            />
                            <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => copy(link)} sx={{ flexShrink: 0 }}>
                                {t('marketing.partners.copy')}
                            </Button>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                                size="small"
                                fullWidth
                                label={t('marketing.partners.qrLink')}
                                value={qrUrl}
                                InputProps={{ readOnly: true }}
                            />
                            <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => copy(qrUrl)} sx={{ flexShrink: 0 }}>
                                {t('marketing.partners.copy')}
                            </Button>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            {t('marketing.partners.linkHelp')}
                        </Typography>
                    </Stack>
                    <Box sx={{ textAlign: 'center' }}>
                        {qr && (
                            <Box component="img" src={qr} alt={t('marketing.partners.qrAlt', { name: record.name })}
                                sx={{ width: 200, height: 200, display: 'block', mx: 'auto', imageRendering: 'pixelated' }} />
                        )}
                        <Button
                            variant="contained"
                            startIcon={<PrintIcon />}
                            disabled={!qr}
                            onClick={() => qr && printCard(qr, record.name, link, t('marketing.partners.printScan'))}
                            sx={{ mt: 1 }}
                        >
                            {t('marketing.partners.print')}
                        </Button>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default PartnerLinkPanel;
